import { Fragment, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {
  DiffRowResult,
  QuestionMatchResult,
  QuestionDiffResponse,
  StructuredQuestion,
} from '../interfaces/questions'
import { QUESTION_FIELD_LABELS } from '../utils/questionDiff'
import QuestionDisplay from './QuestionDisplay'
import CompactQuestionEditor from './CompactQuestionEditor'

interface QuestionDiffViewProps {
  diffResponse: QuestionDiffResponse
  onAcceptQuestions: (questions: StructuredQuestion[]) => void
  onBack: () => void
}

const CONFIDENCE_COLORS: Record<
  string,
  'success' | 'warning' | 'error' | 'default'
> = {
  high: 'success',
  medium: 'warning',
  low: 'error',
}

export default function QuestionDiffView({
  diffResponse,
  onAcceptQuestions,
  onBack,
}: QuestionDiffViewProps) {
  const matches = diffResponse.matches ?? []

  const counts = useMemo(() => {
    const c = { matched_unchanged: 0, matched_changed: 0, added: 0, removed: 0 }
    matches.forEach((m) => {
      if (m.status in c) c[m.status as keyof typeof c]++
    })
    return c
  }, [matches])

  const hasChanges = counts.matched_changed + counts.added + counts.removed > 0

  function normalizeQuestion(q: StructuredQuestion): StructuredQuestion {
    return {
      ...structuredClone(q),
      required: q.required ?? false,
      attachment_required: q.attachment_required ?? false,
    }
  }

  // Editable result state for every row
  const [results, setResults] = useState<Map<number, DiffRowResult>>(() => {
    const map = new Map<number, DiffRowResult>()
    matches.forEach((match, i) => {
      if (match.status === 'matched_unchanged') {
        map.set(i, {
          question: normalizeQuestion(match.existing!),
          included: true,
          source: 'existing',
        })
      } else if (match.status === 'matched_changed') {
        map.set(i, {
          question: normalizeQuestion(match.extracted!),
          included: true,
          source: 'extracted',
        })
      } else if (match.status === 'added') {
        map.set(i, {
          question: normalizeQuestion(match.extracted!),
          included: true,
          source: 'extracted',
        })
      } else if (match.status === 'removed') {
        map.set(i, {
          question: normalizeQuestion(match.existing!),
          included: false,
          source: 'existing',
        })
      }
    })
    return map
  })

  // Which unchanged rows have their comparison columns expanded
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const [showNotes, setShowNotes] = useState(false)
  const [showWarnings, setShowWarnings] = useState(true)

  function handleResultEdit(index: number, updated: StructuredQuestion) {
    setResults((prev) => {
      const next = new Map(prev)
      const row = prev.get(index)
      if (row) {
        next.set(index, { ...row, question: updated, source: 'edited' })
      }
      return next
    })
  }

  function handleSelectSource(
    index: number,
    source: 'existing' | 'extracted',
    match: QuestionMatchResult,
  ) {
    const raw = source === 'existing' ? match.existing! : match.extracted!
    const question = normalizeQuestion(raw)
    setResults((prev) => {
      const next = new Map(prev)
      next.set(index, {
        question,
        included: true,
        source,
      })
      return next
    })
  }

  function handleToggleIncluded(index: number) {
    setResults((prev) => {
      const next = new Map(prev)
      const row = prev.get(index)
      if (row) {
        next.set(index, { ...row, included: !row.included })
      }
      return next
    })
  }

  function toggleExpand(index: number) {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }

  function buildAcceptedQuestions(): StructuredQuestion[] {
    const accepted: StructuredQuestion[] = []
    matches.forEach((_, i) => {
      const row = results.get(i)
      if (row?.included && row.question) {
        accepted.push(row.question)
      }
    })
    return accepted.map((q, i) => ({ ...q, index: i }))
  }

  // Count included questions for the accept button label
  const includedCount = Array.from(results.values()).filter(
    (r) => r.included,
  ).length

  return (
    <Box>
      {/* Summary bar */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            mb: 1,
          }}
        >
          <Chip
            label={`Confidence: ${diffResponse.confidence}`}
            color={CONFIDENCE_COLORS[diffResponse.confidence] ?? 'default'}
            size="small"
          />
          <Chip
            label={`${matches.length} question${
              matches.length !== 1 ? 's' : ''
            } compared`}
            size="small"
            variant="outlined"
          />
          {counts.matched_changed > 0 && (
            <Chip
              label={`${counts.matched_changed} changed`}
              size="small"
              color="warning"
            />
          )}
          {counts.added > 0 && (
            <Chip label={`${counts.added} new`} size="small" color="success" />
          )}
          {counts.removed > 0 && (
            <Chip
              label={`${counts.removed} removed`}
              size="small"
              color="error"
            />
          )}
          {!hasChanges && (
            <Chip
              label="No differences found"
              size="small"
              color="success"
              variant="outlined"
            />
          )}

          <Button
            size="small"
            variant="text"
            endIcon={showNotes ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowNotes((v) => !v)}
            sx={{ ml: 'auto' }}
          >
            Parsing notes
          </Button>
        </Box>

        <Collapse in={showNotes}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {diffResponse.parsing_notes}
          </Typography>
        </Collapse>

        {((diffResponse.warnings && diffResponse.warnings.length > 0) ||
          (diffResponse.ambiguities &&
            diffResponse.ambiguities.length > 0)) && (
          <>
            <Divider sx={{ my: 1 }} />
            <Button
              size="small"
              variant="text"
              color="warning"
              endIcon={showWarnings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowWarnings((v) => !v)}
            >
              Warnings / Ambiguities
            </Button>
            <Collapse in={showWarnings}>
              {diffResponse.warnings?.map((w, i) => (
                <Alert key={`w-${i}`} severity="warning" sx={{ mt: 1 }}>
                  {w}
                </Alert>
              ))}
              {diffResponse.ambiguities?.map((a, i) => (
                <Alert key={`a-${i}`} severity="info" sx={{ mt: 1 }}>
                  {a}
                </Alert>
              ))}
            </Collapse>
          </>
        )}
      </Paper>

      {!hasChanges && (
        <Alert severity="success" sx={{ mb: 2 }}>
          No changes detected — all questions match the extracted document.
        </Alert>
      )}

      {/* Column headers */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={3.5}>
          <Typography variant="subtitle2" fontWeight="bold">
            Current (stored)
          </Typography>
        </Grid>
        <Grid item xs={3.5}>
          <Typography variant="subtitle2" fontWeight="bold">
            Extracted (from document)
          </Typography>
        </Grid>
        <Grid item xs={5}>
          <Typography variant="subtitle2" fontWeight="bold">
            Result (editable)
          </Typography>
        </Grid>
      </Grid>

      {/* All match rows */}
      {matches.map((match, i) => {
        if (match.status === 'matched_unchanged') {
          return (
            <Fragment key={i}>
              <UnchangedRow
                match={match}
                result={results.get(i)!}
                expanded={expandedRows.has(i)}
                onToggleExpand={() => toggleExpand(i)}
                onResultEdit={(q) => handleResultEdit(i, q)}
              />
              <Divider sx={{ my: 2 }} />
            </Fragment>
          )
        }
        if (match.status === 'matched_changed') {
          return (
            <Fragment key={i}>
              <ChangedRow
                match={match}
                result={results.get(i)!}
                onSelectSource={(src) => handleSelectSource(i, src, match)}
                onResultEdit={(q) => handleResultEdit(i, q)}
              />
              <Divider sx={{ my: 2 }} />
            </Fragment>
          )
        }
        if (match.status === 'added') {
          return (
            <Fragment key={i}>
              <AddedRow
                match={match}
                result={results.get(i)!}
                onToggleIncluded={() => handleToggleIncluded(i)}
                onResultEdit={(q) => handleResultEdit(i, q)}
              />
              <Divider sx={{ my: 2 }} />
            </Fragment>
          )
        }
        if (match.status === 'removed') {
          return (
            <Fragment key={i}>
              <RemovedRow
                match={match}
                result={results.get(i)!}
                onToggleIncluded={() => handleToggleIncluded(i)}
                onResultEdit={(q) => handleResultEdit(i, q)}
              />
              <Divider sx={{ my: 2 }} />
            </Fragment>
          )
        }
        return null
      })}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button type="button" variant="text" onClick={onBack}>
          &larr; Back
        </Button>
        <Button
          type="button"
          variant="contained"
          onClick={() => onAcceptQuestions(buildAcceptedQuestions())}
        >
          {hasChanges
            ? `Accept ${includedCount} question${
                includedCount !== 1 ? 's' : ''
              }`
            : 'Confirm \u2014 No Changes'}
        </Button>
      </Box>
    </Box>
  )
}

// ─── UnchangedRow ────────────────────────────────────────────────────────────

function UnchangedRow({
  match,
  result,
  expanded,
  onToggleExpand,
  onResultEdit,
}: {
  match: QuestionMatchResult
  result: DiffRowResult
  expanded: boolean
  onToggleExpand: () => void
  onResultEdit: (q: StructuredQuestion) => void
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Grid container spacing={2} alignItems="flex-start">
        {/* Columns 1-2: collapsible comparison */}
        <Grid item xs={7}>
          {/* Collapsed summary */}
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              bgcolor: 'grey.50',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
            onClick={onToggleExpand}
          >
            <Chip
              label={match.existing!.question_number}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {match.existing!.question}
            </Typography>
            <Chip
              label="No changes"
              size="small"
              color="success"
              variant="outlined"
              sx={{ flexShrink: 0 }}
            />
            <IconButton size="small">
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          </Paper>

          {/* Expanded: side-by-side comparison */}
          <Collapse in={expanded}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                <QuestionDisplay question={match.existing!} />
              </Grid>
              <Grid item xs={6} sx={{ minWidth: 0, overflow: 'hidden' }}>
                <QuestionDisplay question={match.extracted!} />
              </Grid>
            </Grid>
          </Collapse>
        </Grid>

        {/* Column 3: result — always visible */}
        <Grid item xs={5}>
          <CompactQuestionEditor
            question={result.question}
            onChange={onResultEdit}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

// ─── ChangedRow ──────────────────────────────────────────────────────────────

function ChangedRow({
  match,
  result,
  onSelectSource,
  onResultEdit,
}: {
  match: QuestionMatchResult
  result: DiffRowResult
  onSelectSource: (source: 'existing' | 'extracted') => void
  onResultEdit: (q: StructuredQuestion) => void
}) {
  // Use tracked source — 'edited' maps to null (neither toggle selected)
  const toggleValue =
    result.source === 'existing' || result.source === 'extracted'
      ? result.source
      : null

  return (
    <Box
      sx={{
        mb: 2,
        borderLeft: '4px solid',
        borderLeftColor: 'warning.main',
        pl: 1,
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Column 1: current/stored */}
        <Grid item xs={3.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
          <QuestionDisplay question={match.existing!} />
        </Grid>

        {/* Column 2: extracted with highlighted changed fields */}
        <Grid item xs={3.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
          <QuestionDisplay
            question={match.extracted!}
            highlightFields={match.changed_fields}
          />
        </Grid>

        {/* Column 3: editable result */}
        <Grid item xs={5}>
          <Box sx={{ mb: 1 }}>
            <ToggleButtonGroup
              value={toggleValue}
              exclusive
              onChange={(_, value) => {
                if (value) onSelectSource(value)
              }}
              size="small"
            >
              <ToggleButton value="existing">Use Current</ToggleButton>
              <ToggleButton value="extracted">Use Extracted</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <CompactQuestionEditor
            question={result.question}
            onChange={onResultEdit}
          />
        </Grid>
      </Grid>

      {/* Changed field chips + similarity */}
      <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Changed:
        </Typography>
        {match.changed_fields.map((field) => {
          const existing = (match.existing as any)?.[field]
          const extracted = (match.extracted as any)?.[field]
          const label = QUESTION_FIELD_LABELS[field] ?? field
          const suffix =
            !existing && extracted ? ' added'
            : existing && !extracted ? ' removed'
            : ''
          return (
            <Chip
              key={field}
              label={label + suffix}
              size="small"
              variant="outlined"
              color="warning"
              sx={{ height: 18, fontSize: 11 }}
            />
          )
        })}
        {match.similarity < 1 && (
          <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
            Matched by similarity: {Math.round(match.similarity * 100)}%
          </Typography>
        )}
      </Box>
    </Box>
  )
}

// ─── AddedRow ────────────────────────────────────────────────────────────────

function AddedRow({
  match,
  result,
  onToggleIncluded,
  onResultEdit,
}: {
  match: QuestionMatchResult
  result: DiffRowResult
  onToggleIncluded: () => void
  onResultEdit: (q: StructuredQuestion) => void
}) {
  return (
    <Box
      sx={{
        mb: 2,
        borderLeft: '4px solid',
        borderLeftColor: 'success.main',
        pl: 1,
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Column 1: empty placeholder */}
        <Grid item xs={3.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
          <PlaceholderCard label="New question" />
        </Grid>

        {/* Column 2: extracted */}
        <Grid item xs={3.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
          <QuestionDisplay question={match.extracted!} />
        </Grid>

        {/* Column 3: editable result */}
        <Grid item xs={5}>
          <Box sx={{ mb: 1 }}>
            <Button
              type="button"
              size="small"
              variant={result.included ? 'outlined' : 'contained'}
              color={result.included ? 'error' : 'success'}
              onClick={onToggleIncluded}
            >
              {result.included ? 'Exclude' : 'Include'}
            </Button>
          </Box>
          {result.included ? (
            <CompactQuestionEditor
              question={result.question}
              onChange={onResultEdit}
            />
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderStyle: 'dashed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 80,
              }}
            >
              <Typography variant="body2" color="text.disabled">
                Excluded
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

// ─── RemovedRow ──────────────────────────────────────────────────────────────

function RemovedRow({
  match,
  result,
  onToggleIncluded,
  onResultEdit,
}: {
  match: QuestionMatchResult
  result: DiffRowResult
  onToggleIncluded: () => void
  onResultEdit: (q: StructuredQuestion) => void
}) {
  return (
    <Box
      sx={{
        mb: 2,
        borderLeft: '4px solid',
        borderLeftColor: 'error.main',
        pl: 1,
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* Column 1: existing (stored) */}
        <Grid item xs={3.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
          <QuestionDisplay question={match.existing!} />
        </Grid>

        {/* Column 2: empty placeholder */}
        <Grid item xs={3.5} sx={{ minWidth: 0, overflow: 'hidden' }}>
          <PlaceholderCard label="Not in document" isRemoved />
        </Grid>

        {/* Column 3: result */}
        <Grid item xs={5}>
          {result.included ? (
            <>
              <Box sx={{ mb: 1 }}>
                <Button
                  type="button"
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={onToggleIncluded}
                >
                  Remove
                </Button>
              </Box>
              <CompactQuestionEditor
                question={result.question}
                onChange={onResultEdit}
              />
            </>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'error.50',
                borderStyle: 'dashed',
                borderColor: 'error.light',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 80,
                gap: 1,
              }}
            >
              <Typography variant="body2" color="error.main">
                Will be removed
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Before deciding to keep this question, consider if this question
                might have been added as a "new question", as it was not similar
                enough to match to any existing question
              </Typography>
              <Button
                type="button"
                size="small"
                variant="outlined"
                onClick={onToggleIncluded}
              >
                Keep this question
              </Button>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

// ─── PlaceholderCard ─────────────────────────────────────────────────────────

function PlaceholderCard({
  label,
  isRemoved,
}: {
  label: string
  isRemoved?: boolean
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 80,
        bgcolor: isRemoved ? 'error.50' : 'grey.50',
        borderStyle: 'dashed',
        borderColor: isRemoved ? 'error.light' : 'grey.300',
      }}
    >
      <Typography
        variant="body2"
        color={isRemoved ? 'error.main' : 'text.disabled'}
      >
        {label}
      </Typography>
    </Paper>
  )
}
